import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { computed, DestroyRef, inject, Service, signal } from '@angular/core';
import { Router } from '@angular/router';
import {
  AUTH_IMPERSONATION_CACHE_KEY,
  AUTH_REFRESH_TOKEN_CACHE_KEY,
  AUTH_TOKEN_CACHE_KEY,
} from '@core/constants/auth.constants';
import { APP_ROUTES } from '@core/constants/routes.constants';
import { USERS_API, usersApiUrl } from '@core/constants/users-api.constants';
import { V2_EXCLUSIVE_PERMISSIONS } from '@core/constants/version2-permissions.constants';
import { skipErrorToast } from '@core/interceptors/http.interceptor';
import { CacheService } from '@core/services/cache.service';
import { FeatureFlagsService } from '@core/services/feature-flags.service';
import { UIService } from '@core/services/ui.service';
import { jwtDecode as decode } from 'jwt-decode';
import {
  catchError,
  defaultIfEmpty,
  finalize,
  forkJoin,
  map,
  Observable,
  of,
  shareReplay,
  Subject,
  switchMap,
  takeUntil,
  tap,
  throwError,
} from 'rxjs';
import { isSessionInvalidRefreshFailure } from './refresh-error-classifier';
import {
  AccountState,
  IAuthInfo,
  IChangePasswordRequest,
  IImpersonationSession,
  IImpersonationStartResponse,
  IToken,
  IUserAccountInfoV2,
  IUsersLoginRequest,
  IUsersRefreshRequest,
  IUsersResetPasswordRequest,
  IUsersSetPasswordRequest,
} from './types';
import { IUserProfileDto } from './user-profile.type';

@Service()
export class AuthService {
  private readonly isAuthenticated = signal<boolean>(false);
  private readonly currentUserSignal = signal<IUserProfileDto | null>(null);
  private readonly userPermissionsSignal = signal<string[]>([]);
  private readonly accountInfoSignal = signal<IUserAccountInfoV2 | null>(null);
  /** Shown in the nav user menu when profile is not loaded (email from API or JWT). */
  private readonly userMenuLabelSignal = signal<string | null>(null);
  private readonly httpClient = inject(HttpClient);
  private readonly cacheService = inject(CacheService);
  private readonly featureFlags = inject(FeatureFlagsService);
  private readonly router = inject(Router);
  private readonly uiService = inject(UIService);
  private refreshInProgress$: Observable<IToken> | null = null;
  private userAccessRequest$: Observable<boolean> | null = null;
  private userAccessGeneration = 0;
  private readonly userAccessReplaced$ = new Subject<void>();
  private readonly accountStateSignal = signal<AccountState>('loading');
  private readonly sessionExpiredNotified = signal<boolean>(false);

  readonly currentUser = computed(() => this.currentUserSignal());
  readonly userPermissions = computed(() => this.userPermissionsSignal());
  readonly accountInfo = computed(() => this.accountInfoSignal());
  readonly accountState = this.accountStateSignal.asReadonly();
  readonly userMenuLabel = computed(() => this.userMenuLabelSignal());

  /**
   * UI-only impersonation chrome state. Hydrated from minimal localStorage metadata, and only
   * while a valid access token exists. It is not a security authority (backend enforces).
   */
  private readonly impersonationSession = signal<IImpersonationSession | null>(null);
  readonly isImpersonating = computed(() => this.impersonationSession() !== null);
  readonly impersonationTargetName = computed(
    () => this.impersonationSession()?.targetName ?? null
  );
  readonly impersonationExpiresAtUtc = computed(
    () => this.impersonationSession()?.expiresAtUtc ?? null
  );
  /** UX gate only: the backend's `canImpersonate` capability. Backend remains the authority. */
  readonly canStartImpersonation = computed(
    () => this.accountInfoSignal()?.canImpersonate === true && !this.isImpersonating()
  );

  /** UX rule shared by list and details: only active users other than yourself can be impersonated. */
  isImpersonationCandidate(target: { publicId: string; isActive?: boolean | null }): boolean {
    return !!target.isActive && target.publicId !== this.currentUserSignal()?.publicId;
  }

  constructor() {
    this.hydrateImpersonation(this.getAccessToken());
    if (typeof window !== 'undefined') {
      const onStorage = (event: StorageEvent) => this.onStorageEvent(event);
      window.addEventListener('storage', onStorage);
      inject(DestroyRef).onDestroy(() => window.removeEventListener('storage', onStorage));
    }
  }

  authenticate(accessToken?: string): Observable<boolean> {
    const token = accessToken ?? this.getAccessToken();
    if (!token) {
      this.clearImpersonationMetadata();
      return of(false);
    }
    if (!this.hydrateImpersonation(token)) {
      return of(false);
    }
    // Impersonation tokens are opaque and never refreshed; hydrate already validated expiry.
    if (this.isImpersonating() || !this.hasTokenExpired(token)) {
      if (!this.isAuthenticated()) {
        this.isAuthenticated.set(true);
        this.getUserInfoAndPermissions();
      }
      return of(true);
    }
    return this.refreshToken().pipe(
      switchMap(() => {
        if (!this.isAuthenticated()) {
          this.isAuthenticated.set(true);
          this.getUserInfoAndPermissions();
        }
        return of(true);
      }),
      catchError(() => of(false))
    );
  }

  login(email: string, password: string) {
    const body: IUsersLoginRequest = {
      email,
      password,
      twoFactorCode: null,
      twoFactorRecoveryCode: null,
    };
    return this.httpClient.post<IToken>(usersApiUrl(USERS_API.LOGIN), body).pipe(
      tap((value) => {
        // A fresh login is never an impersonation; drop any stale chrome metadata.
        this.clearImpersonationMetadata();
        this.setTokens(value.accessToken, value.refreshToken);
      }),
      switchMap((value) => this.authenticate(value.accessToken))
    );
  }

  /**
   * Starts impersonating `publicId`. Replaces the access token with the impersonation token,
   * removes the refresh token (never kept/used while impersonating; the original Superadmin
   * tokens are NOT stashed — the backend restore cookie handles Stop), persists minimal UI
   * metadata and reloads identity so permissions/nav/profile match the target.
   */
  startImpersonation(publicId: string): Observable<void> {
    return this.httpClient
      .post<IImpersonationStartResponse>(usersApiUrl(USERS_API.IMPERSONATE(publicId)), null, {
        // HttpOnly restore cookie is set by this response. The flow service shows a specific
        // toast (e.g. inactive/locked target), so skip the generic 403 toast.
        withCredentials: true,
        context: skipErrorToast(),
      })
      .pipe(
        tap((response) => {
          this.cacheService.setItem(AUTH_TOKEN_CACHE_KEY, response.accessToken);
          this.cacheService.removeItem(AUTH_REFRESH_TOKEN_CACHE_KEY);
          const session: IImpersonationSession = {
            targetName: response.target.name,
            expiresAtUtc: response.expiresAtUtc,
            targetPublicId: response.target.publicId,
          };
          this.cacheService.setItem(AUTH_IMPERSONATION_CACHE_KEY, { active: true, ...session });
          this.impersonationSession.set(session);
          this.sessionExpiredNotified.set(false);
          this.reloadIdentity();
        }),
        map(() => void 0)
      );
  }

  /**
   * Ends impersonation: restores the original Superadmin access + refresh tokens from the Stop
   * response, clears impersonation metadata and reloads identity. Any Stop failure clears every
   * token and the impersonation chrome and sends the user to login (handoff requirement).
   */
  stopImpersonation(): Observable<void> {
    return this.httpClient
      .post<IToken>(usersApiUrl(USERS_API.STOP_IMPERSONATION), null, {
        // Sends the HttpOnly restore cookie. Failures force re-login, so skip the generic toast.
        withCredentials: true,
        context: skipErrorToast(),
      })
      .pipe(
        tap((tokens) => {
          this.setTokens(tokens.accessToken, tokens.refreshToken);
          this.clearImpersonationMetadata();
          this.reloadIdentity();
        }),
        map(() => void 0),
        catchError((error: unknown) => {
          this.logout();
          return throwError(() => error);
        })
      );
  }

  /**
   * Called when an API call returns 401 while impersonating: the impersonation access token is
   * not refreshable, so the whole local session (tokens + chrome) is cleared. The caller is
   * responsible for navigation / user notice.
   */
  expireImpersonatedSession(): void {
    this.clearSession();
  }

  requestPasswordReset(email: string) {
    return this.httpClient.post<void>(usersApiUrl(USERS_API.FORGOT_PASSWORD), { email });
  }

  setPassword(payload: IUsersSetPasswordRequest) {
    return this.httpClient.post<void>(usersApiUrl(USERS_API.SET_PASSWORD), payload);
  }

  resetPassword(payload: IUsersResetPasswordRequest) {
    return this.httpClient.post<void>(usersApiUrl(USERS_API.RESET_PASSWORD), payload);
  }

  logout() {
    this.clearSession();
    this.router.navigate([APP_ROUTES.AUTH, APP_ROUTES.LOGIN]);
  }

  /** Notifies the caller (once) whether it should surface a "session expired" message. */
  public consumeSessionExpiredNotice(): boolean {
    if (this.sessionExpiredNotified()) {
      return false;
    }
    this.sessionExpiredNotified.set(true);
    return true;
  }

  private clearSession() {
    this.clearImpersonationMetadata();
    if (!this.getAccessToken() && !this.getRefreshToken()) {
      return;
    }
    this.clearToken();
    this.resetIdentityState();
  }

  /** Resets in-memory identity signals (no storage writes). */
  private resetIdentityState() {
    this.isAuthenticated.set(false);
    this.clearIdentitySignals();
    this.invalidateUserAccess();
  }

  private clearIdentitySignals() {
    this.userPermissionsSignal.set([]);
    this.currentUserSignal.set(null);
    this.accountInfoSignal.set(null);
    this.userMenuLabelSignal.set(null);
  }

  /**
   * After an identity switch (Start/Stop): drop stale profile/permission/role signals so no
   * previous-identity UI survives, then reload them for the current bearer.
   */
  private reloadIdentity() {
    this.clearIdentitySignals();
    this.isAuthenticated.set(true);
    this.refreshUserInfo();
  }

  private clearImpersonationMetadata() {
    this.cacheService.removeItem(AUTH_IMPERSONATION_CACHE_KEY);
    this.impersonationSession.set(null);
  }

  private readImpersonationMetadata(): IImpersonationSession | null {
    const raw = this.cacheService.getItem<Partial<IImpersonationSession> & { active?: boolean }>(
      AUTH_IMPERSONATION_CACHE_KEY
    );
    if (
      !raw ||
      typeof raw !== 'object' ||
      raw.active !== true ||
      typeof raw.targetName !== 'string' ||
      !raw.targetName ||
      typeof raw.expiresAtUtc !== 'string' ||
      !raw.expiresAtUtc
    ) {
      return null;
    }
    return {
      targetName: raw.targetName,
      expiresAtUtc: raw.expiresAtUtc,
      ...(typeof raw.targetPublicId === 'string' ? { targetPublicId: raw.targetPublicId } : {}),
    };
  }

  /**
   * Restores impersonation chrome from metadata iff a valid (unexpired) access token is present.
   * Metadata alone never elevates anything. Returns false when stale impersonation state was
   * found and the whole session was cleared (caller should treat as logged out).
   */
  private hydrateImpersonation(token: string): boolean {
    const metadata = this.readImpersonationMetadata();
    if (!metadata) {
      // Corrupt/absent metadata must not linger.
      if (this.cacheService.getItem(AUTH_IMPERSONATION_CACHE_KEY) !== null) {
        this.cacheService.removeItem(AUTH_IMPERSONATION_CACHE_KEY);
      }
      this.impersonationSession.set(null);
      return true;
    }
    // Impersonation access tokens are opaque Data-Protection strings (not JWTs), so expiry is
    // decided from the server-provided expiresAtUtc, never by decoding the token.
    if (token && this.isImpersonationMetadataLive(metadata)) {
      this.setImpersonationSessionIfChanged(metadata);
      return true;
    }
    this.clearSession();
    return false;
  }

  /** True only when `expiresAtUtc` parses and is in the future. */
  private isImpersonationMetadataLive(metadata: IImpersonationSession): boolean {
    const expiresAt = Date.parse(metadata.expiresAtUtc);
    return Number.isFinite(expiresAt) && expiresAt > Date.now();
  }

  private setImpersonationSessionIfChanged(next: IImpersonationSession | null) {
    const current = this.impersonationSession();
    if (
      current?.targetName === next?.targetName &&
      current?.expiresAtUtc === next?.expiresAtUtc &&
      current?.targetPublicId === next?.targetPublicId
    ) {
      return;
    }
    this.impersonationSession.set(next);
  }

  /** Multi-tab sync: shared localStorage, last write wins, no custom protocol. */
  private onStorageEvent(event: StorageEvent) {
    const relevant =
      event.key === null ||
      event.key === AUTH_TOKEN_CACHE_KEY ||
      event.key === AUTH_REFRESH_TOKEN_CACHE_KEY ||
      event.key === AUTH_IMPERSONATION_CACHE_KEY;
    if (!relevant) {
      return;
    }

    const wasImpersonating = this.isImpersonating();
    const token = this.getAccessToken();

    if (!token) {
      // Logged out in another tab. Only react if this tab was showing impersonation chrome;
      // plain logout propagation is unchanged outside impersonation.
      if (wasImpersonating) {
        this.impersonationSession.set(null);
        this.resetIdentityState();
        this.router.navigate([APP_ROUTES.AUTH, APP_ROUTES.LOGIN]);
      }
      return;
    }

    const metadata = this.readImpersonationMetadata();
    if (metadata && !this.isImpersonationMetadataLive(metadata)) {
      this.clearSession();
      this.router.navigate([APP_ROUTES.AUTH, APP_ROUTES.LOGIN]);
      return;
    }

    this.setImpersonationSessionIfChanged(metadata);
    if (wasImpersonating !== this.isImpersonating() && this.isAuthenticated()) {
      // Start/Stop happened in another tab: refresh identity for the new bearer.
      this.reloadIdentity();
    }
  }

  getAccessToken(): string {
    return this.cacheService.getItem<string>(AUTH_TOKEN_CACHE_KEY) ?? '';
  }

  getRefreshToken(): string {
    return this.cacheService.getItem<string>(AUTH_REFRESH_TOKEN_CACHE_KEY) ?? '';
  }

  hasPermission(permission: string): boolean {
    if (!permission) {
      return true;
    }

    if (
      V2_EXCLUSIVE_PERMISSIONS.has(permission) &&
      !this.featureFlags.isEnabled('version2FeaturesV1')
    ) {
      return false;
    }

    const perms = this.userPermissions();
    if (perms.includes(permission)) {
      return true;
    }

    return false;
  }

  changePassword({
    currentPassword,
    newPassword,
    confirmPassword: _confirmPassword,
  }: {
    currentPassword: string;
    newPassword: string;
    confirmPassword: string;
  }) {
    const body: IChangePasswordRequest = { currentPassword, newPassword };
    return this.httpClient.post<void>(usersApiUrl(USERS_API.CHANGE_PASSWORD), body);
  }

  public refreshUserInfo() {
    this.invalidateUserAccess();
    this.userAccessReplaced$.next();
    this.getUserInfoAndPermissions();
  }

  /**
   * Resolves account info and effective permissions for guards. Completes only once the account
   * is `ready`; a failed load errors and is not cached, so the next call retries. A load replaced
   * by an identity change is followed by the current one, or ends as a 401 once signed out.
   */
  ensureUserAccessLoaded$(): Observable<void> {
    if (this.accountStateSignal() === 'ready') {
      return of(void 0);
    }
    return this.getOrCreateUserAccessLoad$().pipe(
      switchMap((isCurrent) => {
        if (isCurrent) {
          return of(void 0);
        }
        return this.isAuthenticated()
          ? this.ensureUserAccessLoaded$()
          : throwError(() => new HttpErrorResponse({ status: 401 }));
      })
    );
  }

  public refreshToken(): Observable<IToken> {
    if (this.refreshInProgress$) {
      return this.refreshInProgress$;
    }
    if (this.isImpersonating() || !this.getRefreshToken()) {
      // Impersonation access tokens are never refreshed (no refresh token is kept).
      this.clearSession();
      return throwError(() => new HttpErrorResponse({ status: 401 }));
    }
    this.uiService.setIsLoading(true);
    const body: IUsersRefreshRequest = {
      refreshToken: this.getRefreshToken(),
    };
    this.refreshInProgress$ = this.httpClient
      .post<IToken>(usersApiUrl(USERS_API.REFRESH), body, { context: skipErrorToast() })
      .pipe(
        tap((value) => this.setTokens(value.accessToken, value.refreshToken)),
        catchError((error: HttpErrorResponse) => {
          if (isSessionInvalidRefreshFailure(error)) {
            this.clearSession();
            return throwError(
              () => new HttpErrorResponse({ status: 401, url: error.url ?? undefined })
            );
          }
          return throwError(() => error);
        }),
        shareReplay(1),
        finalize(() => {
          this.uiService.setIsLoading(false);
          this.refreshInProgress$ = null;
        })
      );
    return this.refreshInProgress$;
  }

  private getUserInfoAndPermissions() {
    this.getOrCreateUserAccessLoad$().subscribe({ error: () => undefined });
  }

  private invalidateUserAccess() {
    this.userAccessGeneration++;
    this.accountStateSignal.set('loading');
    this.userAccessRequest$ = null;
  }

  private getOrCreateUserAccessLoad$(): Observable<boolean> {
    if (!this.userAccessRequest$) {
      const generation = this.userAccessGeneration;
      const isCurrent = () => generation === this.userAccessGeneration;
      this.accountStateSignal.set('loading');
      const request$: Observable<boolean> = this.fetchUserAccess$().pipe(
        takeUntil(this.userAccessReplaced$),
        map(({ profile, account }) => {
          if (!isCurrent()) {
            return false;
          }
          this.currentUserSignal.set(profile);
          this.accountInfoSignal.set(account);
          this.userMenuLabelSignal.set(account.email?.trim() || this.tryEmailFromAccessToken());
          this.userPermissionsSignal.set([...new Set(account.permissions ?? [])]);
          this.accountStateSignal.set('ready');
          return true;
        }),
        defaultIfEmpty(false),
        catchError((error: unknown) => {
          if (!isCurrent()) {
            return of(false);
          }
          this.accountStateSignal.set('failed');
          return throwError(() => error);
        }),
        finalize(() => {
          if (this.userAccessRequest$ === request$) {
            this.userAccessRequest$ = null;
          }
        }),
        shareReplay({ bufferSize: 1, refCount: false })
      );
      this.userAccessRequest$ = request$;
    }
    return this.userAccessRequest$;
  }

  private fetchUserAccess$() {
    return forkJoin({
      profile: this.httpClient
        .get<IUserProfileDto>(usersApiUrl(USERS_API.ME))
        .pipe(catchError(() => of(null))),
      account: this.httpClient.get<IUserAccountInfoV2>(usersApiUrl(USERS_API.MANAGE_INFO_V2), {
        context: skipErrorToast(),
      }),
    });
  }

  private hasTokenExpired(token: string): boolean {
    try {
      const currentUser = this.getAuthInfoFromToken(token);
      return currentUser.exp < Date.now() / 1000;
    } catch {
      return true;
    }
  }

  private setTokens(accessToken: string, refreshToken: string) {
    this.cacheService.setItem(AUTH_TOKEN_CACHE_KEY, accessToken);
    this.cacheService.setItem(AUTH_REFRESH_TOKEN_CACHE_KEY, refreshToken);
    this.sessionExpiredNotified.set(false);
  }

  private getAuthInfoFromToken(token: string): IAuthInfo {
    return decode(token);
  }

  private tryEmailFromAccessToken(): string | null {
    const token = this.getAccessToken();
    if (!token) {
      return null;
    }
    try {
      const info = this.getAuthInfoFromToken(token);
      return info.email?.trim() || null;
    } catch {
      return null;
    }
  }

  private clearToken() {
    this.cacheService.removeItem(AUTH_TOKEN_CACHE_KEY);
    this.cacheService.removeItem(AUTH_REFRESH_TOKEN_CACHE_KEY);
  }
}
